import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Home, 
  Store, 
  ShoppingCart, 
  DollarSign, 
  Package, 
  Calendar, 
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  Edit,
  Eye,
  Ban,
  CheckSquare,
  Clock,
  TrendingUp,
  Users,
  FileText,
  Lock,
  Unlock,
  LogIn,
  ExternalLink,
  Key,
  Copy,
  Trash2,
  Check,
  Image as ImageIcon,
  Upload,
  Plus,
  Search,
  Download,
  FileSpreadsheet,
  X,
  Save
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Switch } from './components/ui/switch';
import { toast } from 'sonner';
import { BACKEND_URL, API } from './utils/apiConfig';

// Master Restaurants List Content
export const MasterRestaurantsContent = ({ onImpersonate }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingPrice, setEditingPrice] = useState(74.90);
  const [savingPrice, setSavingPrice] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, suspended, expired
  const [impersonating, setImpersonating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Função para salvar preço personalizado
  const handleSavePrice = async () => {
    if (!selectedRestaurant) return;
    
    setSavingPrice(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/master/restaurants/${selectedRestaurant.id}/subscription-price`,
        { custom_subscription_price: editingPrice === '' ? null : parseFloat(editingPrice) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Valor da assinatura atualizado!');
      setShowPriceModal(false);
      fetchRestaurants(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao atualizar preço:', error);
      toast.error('Erro ao atualizar valor');
    } finally {
      setSavingPrice(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API}/master/restaurants`);
      setRestaurants(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setLoading(false);
    }
  };

  const toggleSuspend = async (restaurantId, currentStatus) => {
    try {
      await axios.put(`${API}/master/restaurants/${restaurantId}/status`, {
        suspended: !currentStatus
      });
      fetchRestaurants();
    } catch (error) {
      console.error('Error toggling suspend:', error);
      alert('Erro ao atualizar status da loja');
    }
  };

  const updateExpiry = async () => {
    if (!selectedRestaurant || !expiryDate) return;
    
    try {
      await axios.put(`${API}/master/restaurants/${selectedRestaurant.id}/expiry`, {
        expiry_date: expiryDate
      });
      setShowExpiryModal(false);
      setExpiryDate('');
      setSelectedRestaurant(null);
      fetchRestaurants();
    } catch (error) {
      console.error('Error updating expiry:', error);
      alert('Erro ao atualizar data de vencimento');
    }
  };

  const updateNotes = async () => {
    if (!selectedRestaurant) return;
    
    try {
      await axios.put(`${API}/master/restaurants/${selectedRestaurant.id}/notes`, {
        master_notes: notes
      });
      setShowNotesModal(false);
      setNotes('');
      setSelectedRestaurant(null);
      fetchRestaurants();
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Erro ao atualizar observações');
    }
  };

  const resetPassword = async () => {
    if (!selectedRestaurant || !newPassword) {
      alert('Por favor, preencha a nova senha');
      return;
    }
    
    if (newPassword.length < 6) {
      alert('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (!window.confirm(`Deseja realmente redefinir a senha da loja "${selectedRestaurant.name}"?`)) {
      return;
    }
    
    try {
      await axios.put(`${API}/master/restaurants/${selectedRestaurant.id}/password`, {
        new_password: newPassword
      });
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedRestaurant(null);
      alert('Senha redefinida com sucesso!');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Erro ao redefinir senha: ' + (error.response?.data?.detail || error.message));
    }
  };

  const viewDetails = async (restaurant) => {
    try {
      const response = await axios.get(`${API}/master/restaurants/${restaurant.id}`);
      setSelectedRestaurant(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching restaurant details:', error);
      alert('Erro ao carregar detalhes');
    }
  };

  const deleteRestaurant = async () => {
    if (!selectedRestaurant) return;
    
    // Verificar se o texto de confirmação está correto
    if (deleteConfirmText !== selectedRestaurant.name) {
      alert('O nome da loja não corresponde. Digite exatamente o nome da loja para confirmar.');
      return;
    }
    
    setDeleting(true);
    
    try {
      const response = await axios.delete(`${API}/master/restaurants/${selectedRestaurant.id}`);
      
      alert(`✅ Loja deletada com sucesso!\n\nItens removidos: ${response.data.total_items_deleted}\n\nTodos os dados foram permanentemente excluídos.`);
      
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      setSelectedRestaurant(null);
      fetchRestaurants();
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      alert('❌ Erro ao deletar loja: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleImpersonate = async (restaurant) => {
    if (!onImpersonate) {
      alert('Função de acesso ao painel não disponível');
      return;
    }

    if (!window.confirm(`Deseja acessar o painel da loja "${restaurant.name}"?`)) {
      return;
    }

    try {
      setImpersonating(true);
      const response = await axios.post(`${API}/master/restaurants/${restaurant.id}/impersonate`);
      
      // Call the parent handler to switch to restaurant view
      onImpersonate(response.data);
    } catch (error) {
      console.error('Error impersonating restaurant:', error);
      alert('Erro ao acessar painel da loja: ' + (error.response?.data?.detail || error.message));
      setImpersonating(false);
    }
  };

  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         r.subdomain.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return !r.suspended && !r.is_expired;
    if (filterStatus === 'suspended') return r.suspended;
    if (filterStatus === 'expired') return r.is_expired;
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Gestão de Lojas</h1>
        <p className="text-gray-400 mt-1">{restaurants.length} lojas cadastradas</p>
      </div>

      {/* Filters */}
      <div className="glass-card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por nome, email ou subdomínio..."
              className="glass-input w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'active' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Ativas
            </button>
            <button
              onClick={() => setFilterStatus('suspended')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'suspended' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Suspensas
            </button>
            <button
              onClick={() => setFilterStatus('expired')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'expired' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Expiradas
            </button>
          </div>
        </div>
      </div>

      {/* Restaurants Table */}
      <div className="glass-card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left p-4 text-gray-400 font-medium">Loja</th>
              <th className="text-left p-4 text-gray-400 font-medium">Subdomínio</th>
              <th className="text-left p-4 text-gray-400 font-medium">Status</th>
              <th className="text-left p-4 text-gray-400 font-medium">Vencimento</th>
              <th className="text-left p-4 text-gray-400 font-medium">Valor Assinatura</th>
              <th className="text-left p-4 text-gray-400 font-medium">Pedidos</th>
              <th className="text-left p-4 text-gray-400 font-medium">Receita</th>
              <th className="text-right p-4 text-gray-400 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredRestaurants.map((restaurant) => (
              <tr key={restaurant.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                <td className="p-4">
                  <div>
                    <p className="text-white font-medium">{restaurant.name}</p>
                    <p className="text-sm text-gray-400">{restaurant.email}</p>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-gray-300">{restaurant.subdomain}</span>
                </td>
                <td className="p-4">
                  {restaurant.suspended ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                      Suspensa
                    </span>
                  ) : restaurant.is_expired ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
                      Expirada
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                      Ativa
                    </span>
                  )}
                </td>
                <td className="p-4">
                  {restaurant.expiry_date ? (
                    <div>
                      <p className="text-gray-300 text-sm">
                        {new Date(restaurant.expiry_date).toLocaleDateString('pt-BR')}
                      </p>
                      {restaurant.days_until_expiry !== null && (
                        <p className={`text-xs ${
                          restaurant.days_until_expiry < 0 
                            ? 'text-red-400' 
                            : restaurant.days_until_expiry < 7 
                            ? 'text-yellow-400' 
                            : 'text-gray-500'
                        }`}>
                          {restaurant.days_until_expiry < 0 
                            ? `Expirou há ${Math.abs(restaurant.days_until_expiry)} dias` 
                            : `${restaurant.days_until_expiry} dias restantes`}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm">Sem vencimento</span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${restaurant.is_custom_price ? 'text-yellow-400' : 'text-gray-300'}`}>
                      R$ {(restaurant.subscription_price || 74.90).toFixed(2)}
                    </span>
                    {restaurant.is_custom_price && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">
                        Custom
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setEditingPrice(restaurant.subscription_price || 74.90);
                        setShowPriceModal(true);
                      }}
                      className="p-1 text-gray-400 hover:text-yellow-400 hover:bg-yellow-400/10 rounded transition-colors"
                      title="Editar valor da assinatura"
                    >
                      <Edit size={14} />
                    </button>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-gray-300">{restaurant.stats?.orders || 0}</span>
                </td>
                <td className="p-4">
                  <span className="text-green-400 font-medium">
                    R$ {restaurant.stats?.revenue?.toFixed(2) || '0.00'}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleImpersonate(restaurant)}
                      disabled={impersonating}
                      className="p-2 text-purple-400 hover:bg-purple-400/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Acessar Painel da Loja"
                      data-testid={`impersonate-${restaurant.id}`}
                    >
                      {impersonating ? (
                        <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <LogIn size={18} />
                      )}
                    </button>
                    <button
                      onClick={() => viewDetails(restaurant)}
                      className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                      title="Ver detalhes"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setNotes(restaurant.master_notes || '');
                        setShowNotesModal(true);
                      }}
                      className="p-2 text-cyan-400 hover:bg-cyan-400/20 rounded-lg transition-colors"
                      title="Editar observações"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setNewPassword('');
                        setShowPasswordModal(true);
                      }}
                      className="p-2 text-orange-400 hover:bg-orange-400/20 rounded-lg transition-colors"
                      title="Redefinir senha"
                      data-testid={`reset-password-${restaurant.id}`}
                    >
                      <Key size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setExpiryDate(restaurant.expiry_date ? restaurant.expiry_date.split('T')[0] : '');
                        setShowExpiryModal(true);
                      }}
                      className="p-2 text-yellow-400 hover:bg-yellow-400/20 rounded-lg transition-colors"
                      title="Definir vencimento"
                    >
                      <Calendar size={18} />
                    </button>
                    <button
                      onClick={() => toggleSuspend(restaurant.id, restaurant.suspended)}
                      className={`p-2 rounded-lg transition-colors ${
                        restaurant.suspended
                          ? 'text-green-400 hover:bg-green-400/20'
                          : 'text-red-400 hover:bg-red-400/20'
                      }`}
                      title={restaurant.suspended ? 'Ativar' : 'Suspender'}
                    >
                      {restaurant.suspended ? <Unlock size={18} /> : <Lock size={18} />}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setDeleteConfirmText('');
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Deletar loja permanentemente"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

          </tbody>
        </table>
        
        {filteredRestaurants.length === 0 && (
          <div className="text-center py-12">
            <Store size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-400">Nenhuma loja encontrada</p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="glass-modal max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Detalhes da Loja: {selectedRestaurant?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRestaurant && (
            <div className="space-y-6">
              {/* Master Notes */}
              {selectedRestaurant.master_notes && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <FileText className="w-5 h-5 text-cyan-400 mr-2" />
                    <h3 className="text-lg font-semibold text-white">Observações</h3>
                  </div>
                  <p className="text-gray-300 whitespace-pre-wrap">{selectedRestaurant.master_notes}</p>
                </div>
              )}
              
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Email</label>
                  <p className="text-white">{selectedRestaurant.email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Subdomínio</label>
                  <p className="text-white">{selectedRestaurant.subdomain}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Criado em</label>
                  <p className="text-white">
                    {new Date(selectedRestaurant.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Status</label>
                  <p className="text-white">
                    {selectedRestaurant.suspended ? '🔴 Suspensa' : 
                     selectedRestaurant.is_expired ? '🟣 Expirada' : '🟢 Ativa'}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Pedidos</p>
                  <p className="text-2xl font-bold text-white">
                    {selectedRestaurant.detailed_stats?.orders?.total || 0}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Produtos</p>
                  <p className="text-2xl font-bold text-white">
                    {selectedRestaurant.detailed_stats?.products?.total || 0}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Categorias</p>
                  <p className="text-2xl font-bold text-white">
                    {selectedRestaurant.detailed_stats?.categories || 0}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Receita Total</p>
                  <p className="text-2xl font-bold text-green-400">
                    R$ {selectedRestaurant.detailed_stats?.revenue?.total?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>

              {/* Recent Orders */}
              {selectedRestaurant.detailed_stats?.orders?.recent && 
               selectedRestaurant.detailed_stats.orders.recent.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Pedidos Recentes</h3>
                  <div className="space-y-2">
                    {selectedRestaurant.detailed_stats.orders.recent.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex justify-between items-center bg-gray-800/50 p-3 rounded-lg">
                        <div>
                          <p className="text-white font-medium">#{order.order_number}</p>
                          <p className="text-sm text-gray-400">{order.customer_name || 'Cliente'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-medium">R$ {order.total?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{order.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Expiry Modal */}
      <Dialog open={showExpiryModal} onOpenChange={setShowExpiryModal}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Definir Data de Vencimento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Loja: {selectedRestaurant?.name}
              </label>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Data de Vencimento
              </label>
              <input
                type="date"
                className="glass-input w-full"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Deixe vazio para remover a data de vencimento
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExpiryModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={updateExpiry}
                className="glass-button-primary"
              >
                Salvar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Observações da Loja
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Loja: {selectedRestaurant?.name}
              </label>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Observações Internas
              </label>
              <textarea
                className="glass-input w-full resize-none"
                rows={6}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Adicione observações, anotações ou lembretes sobre esta loja..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Estas notas são visíveis apenas para o master admin
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={updateNotes}
                className="glass-button-primary"
              >
                Salvar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-400" />
              Redefinir Senha da Loja
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
              <p className="text-sm text-orange-300">
                <strong>Atenção:</strong> Você está prestes a redefinir a senha de acesso ao painel desta loja.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Loja: <span className="text-white font-semibold">{selectedRestaurant?.name}</span>
              </label>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nova Senha <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                className="glass-input w-full"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha (mínimo 6 caracteres)"
                minLength={6}
                data-testid="new-password-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                A senha deve ter no mínimo 6 caracteres
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setSelectedRestaurant(null);
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={resetPassword}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
                disabled={!newPassword || newPassword.length < 6}
                data-testid="confirm-reset-password"
              >
                Redefinir Senha
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <Trash2 className="text-red-500" size={28} />
              Deletar Loja Permanentemente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-500/10 border-2 border-red-500 rounded-lg">
              <p className="text-red-400 font-semibold mb-2">⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!</p>
              <p className="text-gray-300 text-sm mb-2">Ao deletar esta loja, os seguintes dados serão PERMANENTEMENTE excluídos:</p>
              <ul className="text-gray-400 text-sm space-y-1 ml-4">
                <li>• Todos os produtos e categorias</li>
                <li>• Todos os adicionais e grupos de adicionais</li>
                <li>• Todos os pedidos e histórico</li>
                <li>• Todas as mesas e configurações</li>
                <li>• Todos os cupons e zonas de entrega</li>
                <li>• Todos os usuários e clientes</li>
                <li>• Todos os logs e configurações</li>
              </ul>
            </div>

            {selectedRestaurant && (
              <div>
                <p className="text-gray-300 mb-2">
                  Para confirmar, digite o nome da loja exatamente como aparece abaixo:
                </p>
                <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 mb-3">
                  <p className="text-white font-bold text-center">{selectedRestaurant.name}</p>
                </div>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Digite o nome da loja aqui"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
                  autoFocus
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={deleteRestaurant}
                disabled={deleting || deleteConfirmText !== selectedRestaurant?.name}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deletando...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Deletar Permanentemente
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Edit Modal */}
      <Dialog open={showPriceModal} onOpenChange={setShowPriceModal}>
        <DialogContent className="glass-modal max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <DollarSign className="text-yellow-400" size={24} />
              Valor da Assinatura
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedRestaurant && (
              <>
                <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <p className="text-sm text-gray-400">Loja:</p>
                  <p className="text-white font-semibold">{selectedRestaurant.name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Valor Mensal (R$)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingPrice}
                      onChange={(e) => setEditingPrice(e.target.value)}
                      placeholder="74.90"
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-lg font-semibold focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Deixe vazio ou 0 para usar o valor padrão global
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowPriceModal(false)}
                    disabled={savingPrice}
                    className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setEditingPrice('');
                      handleSavePrice();
                    }}
                    disabled={savingPrice}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Usar Padrão
                  </button>
                  <button
                    onClick={handleSavePrice}
                    disabled={savingPrice}
                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingPrice ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Check size={16} />
                        Salvar
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================================
// MASTER ANALYTICS CONTENT (Gráficos e Métricas Avançadas)
// ============================================================================

export const MasterAnalyticsContent = () => {
  const [timeline, setTimeline] = useState([]);
  const [topRestaurants, setTopRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [timelineRes, comparisonRes] = await Promise.all([
        axios.get(`${API}/master/analytics/timeline?days=${days}`),
        axios.get(`${API}/master/analytics/comparison?limit=10`)
      ]);
      
      setTimeline(timelineRes.data.timeline || []);
      setTopRestaurants(comparisonRes.data.top_by_revenue || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...timeline.map(d => d.revenue), 1);
  const maxOrders = Math.max(...timeline.map(d => d.orders), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Análises e Métricas</h1>
          <p className="text-gray-400 mt-1">Visualize o desempenho do sistema</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="glass-input"
        >
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </div>

      {/* Revenue Timeline Chart */}
      <div className="glass-card">
        <h2 className="text-xl font-semibold text-white mb-4">Evolução da Receita</h2>
        <div className="space-y-2">
          {timeline.map((item, index) => {
            const heightPercent = (item.revenue / maxRevenue) * 100;
            return (
              <div key={index} className="flex items-center gap-3">
                <div className="w-24 text-sm text-gray-400">
                  {new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                </div>
                <div className="flex-1">
                  <div className="relative h-8 bg-gray-800 rounded-lg overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-300"
                      style={{ width: `${heightPercent}%` }}
                    />
                    <span className="absolute inset-0 flex items-center px-3 text-sm font-medium text-white">
                      R$ {item.revenue.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="w-20 text-sm text-gray-400 text-right">
                  {item.orders} pedidos
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Restaurants */}
      <div className="glass-card">
        <h2 className="text-xl font-semibold text-white mb-4">Top 10 Lojas por Receita</h2>
        <div className="space-y-3">
          {topRestaurants.map((restaurant, index) => (
            <div key={restaurant.id} className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 font-bold text-white">
                {index + 1}
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{restaurant.name}</p>
                <p className="text-sm text-gray-400">
                  {restaurant.orders} pedidos • Ticket médio: R$ {restaurant.avg_order_value.toFixed(2)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-green-400 font-bold text-lg">R$ {restaurant.revenue.toFixed(2)}</p>
                <p className="text-xs text-gray-500">{restaurant.orders_last_7_days} pedidos (7 dias)</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// MASTER REPORTS CONTENT (Relatórios Financeiros)
// ============================================================================

export const MasterReportsContent = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/reports/financial`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setReport(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Relatórios Financeiros</h1>
        <p className="text-gray-400 mt-1">Análise consolidada de faturamento</p>
      </div>

      {/* Date Range Selector */}
      <div className="glass-card">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-300 mb-2">Data Início</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="glass-input w-full"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-300 mb-2">Data Fim</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="glass-input w-full"
            />
          </div>
          <button onClick={fetchReport} className="glass-button-primary">
            <FileText size={18} className="mr-2" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <DollarSign className="w-5 h-5 text-green-400 mr-2" />
            <span className="text-gray-400 text-sm">Receita Total</span>
          </div>
          <p className="text-3xl font-bold text-white">R$ {report?.period?.total_revenue?.toFixed(2) || '0.00'}</p>
        </div>
        
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <ShoppingCart className="w-5 h-5 text-blue-400 mr-2" />
            <span className="text-gray-400 text-sm">Total de Pedidos</span>
          </div>
          <p className="text-3xl font-bold text-white">{report?.period?.total_orders || 0}</p>
        </div>
        
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <TrendingUp className="w-5 h-5 text-purple-400 mr-2" />
            <span className="text-gray-400 text-sm">Ticket Médio</span>
          </div>
          <p className="text-3xl font-bold text-white">R$ {report?.period?.avg_order_value?.toFixed(2) || '0.00'}</p>
        </div>
        
        <div className="glass-card">
          <div className="flex items-center mb-2">
            <Store className="w-5 h-5 text-orange-400 mr-2" />
            <span className="text-gray-400 text-sm">Lojas Ativas</span>
          </div>
          <p className="text-3xl font-bold text-white">{report?.summary?.active_restaurants || 0}</p>
        </div>
      </div>

      {/* Revenue by Restaurant */}
      <div className="glass-card">
        <h2 className="text-xl font-semibold text-white mb-4">Receita por Loja</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-3 text-gray-400 font-medium">#</th>
                <th className="text-left p-3 text-gray-400 font-medium">Loja</th>
                <th className="text-right p-3 text-gray-400 font-medium">Pedidos</th>
                <th className="text-right p-3 text-gray-400 font-medium">Receita</th>
                <th className="text-right p-3 text-gray-400 font-medium">Ticket Médio</th>
              </tr>
            </thead>
            <tbody>
              {report?.by_restaurant?.map((item, index) => (
                <tr key={item.restaurant_id} className="border-b border-gray-800 hover:bg-gray-800/30">
                  <td className="p-3 text-gray-400">{index + 1}</td>
                  <td className="p-3 text-white">{item.restaurant_name}</td>
                  <td className="p-3 text-right text-gray-300">{item.orders}</td>
                  <td className="p-3 text-right text-green-400 font-medium">R$ {item.revenue.toFixed(2)}</td>
                  <td className="p-3 text-right text-gray-300">R$ {item.avg_order_value.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// MASTER TEMPLATES CONTENT (Templates de Configuração)
// ============================================================================

export const MasterTemplatesContent = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    config_data: {}
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/master/templates`);
      setTemplates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/master/templates/${editingTemplate.id}`, formData);
      } else {
        await axios.post(`${API}/master/templates`, formData);
      }
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ name: '', description: '', config_data: {} });
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Erro ao salvar template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) return;
    
    try {
      await axios.delete(`${API}/master/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Erro ao excluir template');
    }
  };

  const openModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        config_data: template.config_data || {}
      });
    } else {
      setEditingTemplate(null);
      setFormData({ name: '', description: '', config_data: {} });
    }
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Templates de Configuração</h1>
          <p className="text-gray-400 mt-1">Gerencie configurações predefinidas para novas lojas</p>
        </div>
        <button onClick={() => openModal()} className="glass-button-primary">
          <CheckSquare size={18} className="mr-2" />
          Novo Template
        </button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="glass-card">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-1">{template.name}</h3>
                <p className="text-sm text-gray-400">{template.description}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openModal(template)}
                  className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                >
                  <XCircle size={16} />
                </button>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-500">
                Criado em {new Date(template.created_at).toLocaleDateString('pt-BR')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {Object.keys(template.config_data || {}).length} configurações
              </p>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="glass-card text-center py-12">
          <CheckSquare size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum template encontrado</h3>
          <p className="text-gray-400 mb-6">Crie templates para aplicar configurações padrão em novas lojas</p>
          <button onClick={() => openModal()} className="glass-button-primary">
            Criar Primeiro Template
          </button>
        </div>
      )}

      {/* Template Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingTemplate ? 'Editar Template' : 'Novo Template'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome</label>
              <input
                type="text"
                required
                className="glass-input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Nome do template"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <textarea
                className="glass-input w-full resize-none"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrição do template"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Configurações (JSON)
              </label>
              <textarea
                className="glass-input w-full resize-none font-mono text-sm"
                rows={8}
                value={JSON.stringify(formData.config_data, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData({...formData, config_data: parsed});
                  } catch (err) {
                    // Invalid JSON, just update the text
                  }
                }}
                placeholder='{"delivery_fee": 5.00, "minimum_order": 10.00}'
              />
              <p className="text-xs text-gray-500 mt-1">
                Exemplo: {`{"delivery_fee": 5.00, "minimum_order": 10.00, "phone": "+5511999999999"}`}
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingTemplate ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================================
// MASTER ACTION LOGS CONTENT (Histórico de Ações)
// ============================================================================

export const MasterActionLogsContent = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [page, filterType]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: 50 };
      if (filterType) params.action_type = filterType;
      
      const response = await axios.get(`${API}/master/action-logs`, { params });
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLoading(false);
    }
  };

  const actionTypeLabels = {
    suspend: '🔴 Suspender',
    activate: '🟢 Ativar',
    update_expiry: '📅 Atualizar Vencimento',
    update_notes: '📝 Atualizar Notas',
    impersonate: '👤 Acessar Painel',
    create_template: '➕ Criar Template',
    update_template: '✏️ Atualizar Template',
    delete_template: '🗑️ Deletar Template',
    apply_template: '✅ Aplicar Template'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Histórico de Ações</h1>
          <p className="text-gray-400 mt-1">Registro de todas as ações do master admin</p>
        </div>
        <select
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setPage(1);
          }}
          className="glass-input"
        >
          <option value="">Todas as ações</option>
          <option value="suspend">Suspensões</option>
          <option value="activate">Ativações</option>
          <option value="update_expiry">Vencimentos</option>
          <option value="impersonate">Acessos</option>
          <option value="update_notes">Notas</option>
        </select>
      </div>

      {/* Logs Timeline */}
      <div className="glass-card">
        <div className="space-y-3">
          {logs.map((log) => (
            <div key={log.id} className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors">
              <div className="flex-shrink-0">
                <Clock className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-sm font-medium text-white">
                    {actionTypeLabels[log.action_type] || log.action_type}
                  </span>
                  {log.restaurant_name && (
                    <span className="text-sm text-blue-400">
                      {log.restaurant_name}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400">{log.description}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(log.created_at).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-700">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 glass-button-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-gray-400">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 glass-button-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        )}
      </div>

      {logs.length === 0 && (
        <div className="glass-card text-center py-12">
          <Clock size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhuma ação registrada</h3>
          <p className="text-gray-400">O histórico de ações aparecerá aqui</p>
        </div>
      )}
    </div>
  );
};


// =============================================================================
// MASTER SYSTEM SETTINGS CONTENT - Global Mercado Pago Configuration
// =============================================================================
// WhatsApp API Config Component (Evolution API ou WAHA)
const EvolutionAPIConfigSection = () => {
  const [config, setConfig] = useState({
    evolution_api_url: '',
    evolution_api_key: '',
    enabled: true,
    api_type: 'evolution' // 'evolution' ou 'waha'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/evolution-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setConfigured(true);
        setConfig(prev => ({
          ...prev,
          evolution_api_url: response.data.evolution_api_url,
          enabled: response.data.enabled,
          api_type: response.data.api_type || 'evolution'
        }));
        setMaskedKey(response.data.evolution_api_key_masked);
      }
    } catch (err) {
      console.error('Error loading WhatsApp API config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      if (!config.evolution_api_url) {
        toast.error('URL da API é obrigatória');
        return;
      }

      await axios.put(`${API}/master/evolution-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const apiName = config.api_type === 'waha' ? 'WAHA' : config.api_type === 'uazapi' ? 'UazAPI' : 'Evolution API';
      toast.success(`✅ Configuração ${apiName} salva com sucesso!`);
      setConfig(prev => ({
        ...prev,
        evolution_api_key: '',
      }));
      setShowKey(false);

      setTimeout(() => loadConfig(), 1000);
    } catch (err) {
      console.error('Error saving WhatsApp API config:', err);
      toast.error(err.response?.data?.detail || '❌ Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="animate-pulse h-32 bg-gray-800/50 rounded-lg"></div>;

  const isWaha = config.api_type === 'waha';
  const isUazapi = config.api_type === 'uazapi';
  const apiName = isWaha ? 'WAHA' : isUazapi ? 'UazAPI' : 'Evolution API';
  const apiColor = isWaha ? 'blue' : isUazapi ? 'purple' : 'green';

  return (
    <div className="glass-card space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp API ({apiName})
        </h2>
        <p className="text-gray-400 text-sm">
          Configure as credenciais para integração WhatsApp. Escolha entre Evolution API, WAHA ou UazAPI.
        </p>
      </div>

      {/* API Type Selector */}
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Tipo de API WhatsApp
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setConfig({...config, api_type: 'evolution'})}
            className={`p-4 rounded-lg border-2 transition-all ${
              !isWaha && !isUazapi
                ? 'border-green-500 bg-green-500/10 text-green-300' 
                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span className="font-semibold text-sm">Evolution API</span>
              <span className="text-xs opacity-70">Open Source</span>
            </div>
          </button>
          <button
            onClick={() => setConfig({...config, api_type: 'waha'})}
            className={`p-4 rounded-lg border-2 transition-all ${
              isWaha 
                ? 'border-blue-500 bg-blue-500/10 text-blue-300' 
                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM6 10h2v2H6zm0 4h8v2H6zm10 0h2v2h-2zm-6-4h8v2h-8z"/>
              </svg>
              <span className="font-semibold">WAHA</span>
              <span className="text-xs opacity-70">WhatsApp HTTP API</span>
            </div>
          </button>
          <button
            onClick={() => setConfig({...config, api_type: 'uazapi'})}
            className={`p-4 rounded-lg border-2 transition-all ${
              isUazapi 
                ? 'border-purple-500 bg-purple-500/10 text-purple-300' 
                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              <span className="font-semibold text-sm">UazAPI</span>
              <span className="text-xs opacity-70">Cloud BR</span>
            </div>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          {isWaha 
            ? '💡 WAHA: API estável com dashboard integrado. Requer Docker.' 
            : isUazapi
            ? '💡 UazAPI: API brasileira cloud. Sem necessidade de servidor próprio.'
            : '💡 Evolution API: Popular e open source. Suporta múltiplas instâncias.'}
        </p>
      </div>

      {configured && (
        <div className={`${isWaha ? 'bg-blue-500/10 border-blue-500/30' : isUazapi ? 'bg-purple-500/10 border-purple-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg p-4`}>
          <div className="flex items-center gap-3">
            <CheckCircle className={`w-5 h-5 ${isWaha ? 'text-blue-400' : isUazapi ? 'text-purple-400' : 'text-green-400'} flex-shrink-0`} />
            <div>
              <p className={`${isWaha ? 'text-blue-300' : isUazapi ? 'text-purple-300' : 'text-green-300'} font-semibold`}>{apiName} Configurada</p>
              <p className={`${isWaha ? 'text-blue-400/80' : isUazapi ? 'text-purple-400/80' : 'text-green-400/80'} text-sm`}>Lojistas podem conectar suas instâncias</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          URL da {apiName}
        </label>
        <input
          type="text"
          value={config.evolution_api_url}
          onChange={(e) => setConfig({...config, evolution_api_url: e.target.value})}
          placeholder={isWaha ? "https://seu-waha.com" : isUazapi ? "https://api.uazapi.dev" : "https://sua-evolution-api.com"}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          {isWaha 
            ? 'URL do servidor WAHA (sem barra no final). Ex: https://waha.seusite.com' 
            : isUazapi
            ? 'URL da API UazAPI. Ex: https://api.uazapi.dev'
            : 'URL base da sua instância Evolution API (sem barra no final)'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {isWaha ? 'X-Api-Key' : isUazapi ? 'Bearer Token' : 'API Key Global'}
        </label>
        {configured && !showKey ? (
          <div className="flex gap-2">
            <div className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2">
              <span className="text-gray-400 text-sm font-mono">{maskedKey}</span>
            </div>
            <button
              onClick={() => setShowKey(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all"
            >
              Atualizar
            </button>
          </div>
        ) : (
          <input
            type="text"
            value={config.evolution_api_key}
            onChange={(e) => setConfig({...config, evolution_api_key: e.target.value})}
            placeholder={isWaha ? "Sua X-Api-Key do WAHA" : isUazapi ? "Seu token de acesso UazAPI" : "Sua API Key global da Evolution"}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
          />
        )}
        <p className="text-xs text-gray-500 mt-1">
          {isWaha 
            ? 'Chave configurada na variável WHATSAPP_API_KEY do WAHA' 
            : isUazapi
            ? 'Token Bearer obtido no painel UazAPI'
            : 'API Key mestra para gerenciar todas as instâncias'}
        </p>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div>
          <label className="text-white font-medium">Ativar {apiName}</label>
          <p className="text-gray-400 text-sm">Permitir que lojistas conectem WhatsApp</p>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
        />
      </div>

      <div className={`${isWaha ? 'bg-blue-500/10 border-blue-500/30' : isUazapi ? 'bg-purple-500/10 border-purple-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg p-4`}>
        <p className={`${isWaha ? 'text-blue-300' : isUazapi ? 'text-purple-300' : 'text-green-300'} text-sm`}>
          {isWaha ? (
            <>
              ℹ️ <strong>WAHA:</strong> Configure o webhook global no WAHA apontando para: <code className="bg-gray-800 px-2 py-1 rounded text-xs">{window.location.origin}/api/webhook/whatsapp</code>
            </>
          ) : isUazapi ? (
            <>
              ℹ️ <strong>UazAPI:</strong> Configure o webhook de cada instância para: <code className="bg-gray-800 px-2 py-1 rounded text-xs">{window.location.origin}/api/webhook/whatsapp</code>
            </>
          ) : (
            <>
              ℹ️ <strong>Como funciona:</strong> Você configura a Evolution API uma vez aqui, e cada lojista poderá conectar sua própria instância WhatsApp através do painel deles, sem precisar de credenciais adicionais.
            </>
          )}
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving || !config.evolution_api_url}
        className={`w-full ${isWaha 
          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600' 
          : isUazapi
          ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
          : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
        } text-white font-bold py-3 px-6 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2`}
      >
        {saving ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Salvando...
          </>
        ) : (
          <>
            <CheckCircle className="w-5 h-5" />
            Salvar {apiName}
          </>
        )}
      </button>
    </div>
  );
};

// WhatsApp AI Config Component
const WhatsAppAIConfigSection = () => {
  const [config, setConfig] = useState({
    zapi_instance_id: '',
    zapi_token: '',
    zapi_security_token: '',
    gemini_api_key: '',
    enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [maskedData, setMaskedData] = useState({
    instance: '',
    token: '',
    security: '',
    gemini: ''
  });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/whatsapp-ai-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setConfigured(true);
        setMaskedData({
          instance: response.data.zapi_instance_id_masked,
          token: response.data.zapi_token_masked,
          security: response.data.zapi_security_token_masked,
          gemini: response.data.gemini_api_key_masked
        });
        setConfig(prev => ({ ...prev, enabled: response.data.enabled }));
      }
      
      setWebhookUrl(response.data.webhook_url);
    } catch (err) {
      console.error('Error loading WhatsApp config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/master/whatsapp-ai-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('✅ Configuração salva com sucesso!');
      setConfig({
        zapi_instance_id: '',
        zapi_token: '',
        zapi_security_token: '',
        gemini_api_key: '',
        enabled: config.enabled
      });
      
      setTimeout(() => loadConfig(), 1000);
    } catch (err) {
      console.error('Error saving WhatsApp config:', err);
      toast.error('❌ Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success('✅ URL copiada!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="animate-pulse h-32 bg-gray-800/50 rounded-lg"></div>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp AI (Z-API + Gemini)
        </h2>
        <p className="text-gray-400 text-sm">
          Configure integração com Z-API e Gemini para receber pedidos via WhatsApp automaticamente
          <span className="ml-2 text-yellow-400">⚠️ Apenas para loja: teste1@gmail.com</span>
        </p>
      </div>

      {/* Webhook URL */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <label className="block text-sm font-medium text-blue-300 mb-2">
          Webhook URL (Cadastre na Z-API)
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={webhookUrl}
            readOnly
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm font-mono"
          />
          <button
            onClick={copyWebhook}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors flex items-center gap-2"
          >
            {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
        </div>
      </div>

      {/* Z-API Fields */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Z-API Instance ID
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.instance}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.zapi_instance_id}
              onChange={(e) => setConfig({...config, zapi_instance_id: e.target.value})}
              placeholder="Seu Instance ID"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Z-API Token
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.token}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.zapi_token}
              onChange={(e) => setConfig({...config, zapi_token: e.target.value})}
              placeholder="Seu Token"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Z-API Security Token
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.security}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.zapi_security_token}
              onChange={(e) => setConfig({...config, zapi_security_token: e.target.value})}
              placeholder="Security Token"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Gemini API Key
          </label>
          {configured && !showTokens ? (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-gray-400 text-sm font-mono">{maskedData.gemini}</span>
            </div>
          ) : (
            <input
              type="text"
              value={config.gemini_api_key}
              onChange={(e) => setConfig({...config, gemini_api_key: e.target.value})}
              placeholder="Sua API Key do Gemini"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
            />
          )}
        </div>
      </div>

      {/* Enable Switch */}
      <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div>
          <label className="text-white font-medium">Ativar WhatsApp AI</label>
          <p className="text-gray-400 text-sm">Sistema receberá e processará mensagens automaticamente</p>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
        />
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <p className="text-blue-300 text-sm">
          ℹ️ <strong>Credenciais pré-configuradas:</strong> As credenciais já estão salvas no sistema como padrão. Você pode alterá-las aqui se necessário.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 px-6 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Salvando...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Salvar WhatsApp AI
            </>
          )}
        </button>

        {configured && !showTokens && (
          <button
            onClick={() => setShowTokens(true)}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all"
          >
            Atualizar
          </button>
        )}
      </div>
    </div>
  );
};

// ========== LOGS DE ERROS DA IA ==========
export const MasterAIErrorLogsContent = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterResolved, setFilterResolved] = useState(null);
  const [filterRestaurant, setFilterRestaurant] = useState('');
  
  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, filterResolved, filterRestaurant]);
  
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API}/master/ai-error-logs?page=${page}&limit=20`;
      
      if (filterResolved !== null) {
        url += `&resolved=${filterResolved}`;
      }
      if (filterRestaurant) {
        url += `&restaurant_id=${filterRestaurant}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      toast.error('Erro ao carregar logs de erros');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/ai-error-stats?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(prevStats => ({ ...prevStats, ...response.data }));
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };
  
  const handleResolve = async (errorId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/master/ai-error-logs/${errorId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Erro marcado como resolvido');
      fetchLogs();
    } catch (error) {
      console.error('Erro ao resolver:', error);
      toast.error('Erro ao marcar como resolvido');
    }
  };
  
  const handleDelete = async (errorId) => {
    if (!window.confirm('Tem certeza que deseja excluir este log?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/master/ai-error-logs/${errorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Log excluído');
      fetchLogs();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir log');
    }
  };
  
  const handleClearResolved = async () => {
    if (!window.confirm('Tem certeza que deseja excluir TODOS os erros resolvidos?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/master/ai-error-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      console.error('Erro ao limpar:', error);
      toast.error('Erro ao limpar logs');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };
  
  const viewDetail = (error) => {
    setSelectedError(error);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-red-400" />
            Logs de Erros da IA
          </h2>
          <p className="text-gray-400 mt-1">Monitore e resolva erros do sistema de IA</p>
        </div>
        <button
          onClick={handleClearResolved}
          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Limpar Resolvidos
        </button>
      </div>
      
      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Erros</p>
              <p className="text-2xl font-bold text-white">{stats?.total_errors || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Não Resolvidos</p>
              <p className="text-2xl font-bold text-yellow-400">{stats?.unresolved || stats?.unresolved_errors || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Hoje</p>
              <p className="text-2xl font-bold text-blue-400">{stats?.today || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Taxa de Resolução</p>
              <p className="text-2xl font-bold text-green-400">
                {stats?.total_errors > 0 
                  ? Math.round(((stats.total_errors - (stats.unresolved || 0)) / stats.total_errors) * 100)
                  : 100}%
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filtros */}
      <div className="flex gap-4 items-center bg-gray-800/30 p-4 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm">Status:</span>
          <select
            value={filterResolved === null ? 'all' : filterResolved.toString()}
            onChange={(e) => {
              const val = e.target.value;
              setFilterResolved(val === 'all' ? null : val === 'true');
              setPage(1);
            }}
            className="bg-gray-700 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Todos</option>
            <option value="false">Não Resolvidos</option>
            <option value="true">Resolvidos</option>
          </select>
        </div>
        
        <button
          onClick={() => { setPage(1); fetchLogs(); }}
          className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition"
        >
          Atualizar
        </button>
      </div>
      
      {/* Lista de Erros */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            Carregando...
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
            <p>Nenhum erro encontrado!</p>
            <p className="text-sm mt-1">A IA está funcionando perfeitamente.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Data/Hora</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Restaurante</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Tipo de Erro</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Mensagem</th>
                  <th className="px-4 py-3 text-left text-gray-300 text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-right text-gray-300 text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-700/30 transition">
                    <td className="px-4 py-3 text-gray-300 text-sm whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3 text-white text-sm">
                      {log.restaurant_name || 'Desconhecido'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-mono">
                        {log.error_type || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm max-w-xs truncate">
                      {log.error_message?.substring(0, 50)}...
                    </td>
                    <td className="px-4 py-3">
                      {log.resolved ? (
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Resolvido
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-400 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          Pendente
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewDetail(log)}
                          className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!log.resolved && (
                          <button
                            onClick={() => handleResolve(log.id)}
                            className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition"
                            title="Marcar como resolvido"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(log.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-700/50">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="text-gray-400">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        )}
      </div>
      
      {/* Modal de Detalhes */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Detalhes do Erro
            </DialogTitle>
          </DialogHeader>
          
          {selectedError && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Data/Hora</p>
                  <p className="text-white">{formatDate(selectedError.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Restaurante</p>
                  <p className="text-white">{selectedError.restaurant_name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Telefone do Cliente</p>
                  <p className="text-white font-mono">{selectedError.customer_phone}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Tipo de Erro</p>
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm font-mono">
                    {selectedError.error_type}
                  </span>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-1">Mensagem do Cliente</p>
                <div className="bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-white text-sm">{selectedError.customer_message || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-1">Mensagem de Erro</p>
                <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg">
                  <p className="text-red-400 text-sm font-mono">{selectedError.error_message}</p>
                </div>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-1">Traceback</p>
                <div className="bg-gray-900 p-3 rounded-lg max-h-48 overflow-y-auto">
                  <pre className="text-gray-300 text-xs font-mono whitespace-pre-wrap">
                    {selectedError.traceback || 'N/A'}
                  </pre>
                </div>
              </div>
              
              {selectedError.resolved && (
                <div className="bg-green-500/10 border border-green-500/30 p-3 rounded-lg">
                  <p className="text-green-400 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Resolvido em {formatDate(selectedError.resolved_at)} por {selectedError.resolved_by}
                  </p>
                  {selectedError.notes && (
                    <p className="text-gray-300 text-sm mt-2">Notas: {selectedError.notes}</p>
                  )}
                </div>
              )}
              
              {!selectedError.resolved && (
                <button
                  onClick={() => {
                    handleResolve(selectedError.id);
                    setShowDetailModal(false);
                  }}
                  className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Marcar como Resolvido
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const MasterSystemSettingsContent = () => {
  const [settings, setSettings] = useState({
    mp_access_token_global: '',
    mp_public_key_global: '',
    subscription_price: 99.00,
    menu_base_url: ''  // URL base para links do cardápio nas mensagens de saudação
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [maskedData, setMaskedData] = useState({
    token: '',
    key: ''
  });

  // Estados para Instruções Globais da IA (Master)
  const [globalAiInstructions, setGlobalAiInstructions] = useState('');
  const [globalAiInstructionsEnabled, setGlobalAiInstructionsEnabled] = useState(true);
  const [savingAiInstructions, setSavingAiInstructions] = useState(false);
  const [aiInstructionsSuccess, setAiInstructionsSuccess] = useState('');
  const [aiInstructionsError, setAiInstructionsError] = useState('');

  // Estados para Deepgram (Transcrição de Áudio)
  const [deepgramConfig, setDeepgramConfig] = useState({
    enabled: false,
    deepgram_api_key: ''
  });
  const [deepgramConfigured, setDeepgramConfigured] = useState(false);
  const [deepgramMaskedKey, setDeepgramMaskedKey] = useState('');
  const [savingDeepgram, setSavingDeepgram] = useState(false);
  const [deepgramSuccess, setDeepgramSuccess] = useState('');
  const [deepgramError, setDeepgramError] = useState('');

  // Estados para alteração de senha do master
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  // Estados para sincronização de pagamentos
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [diagnostico, setDiagnostico] = useState(null);
  const [loadingDiagnostico, setLoadingDiagnostico] = useState(false);

  useEffect(() => {
    loadSettings();
    loadDeepgramConfig();
  }, []);

  const loadDeepgramConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/deepgram-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setDeepgramConfigured(true);
        setDeepgramMaskedKey(response.data.deepgram_api_key_masked || '');
        setDeepgramConfig(prev => ({
          ...prev,
          enabled: response.data.enabled
        }));
      }
    } catch (err) {
      console.error('Error loading Deepgram config:', err);
    }
  };

  const handleSaveDeepgram = async () => {
    try {
      setSavingDeepgram(true);
      setDeepgramError('');
      setDeepgramSuccess('');

      if (!deepgramConfig.deepgram_api_key && !deepgramConfigured) {
        setDeepgramError('API Key do Deepgram é obrigatória');
        return;
      }

      const token = localStorage.getItem('token');
      
      const updateData = {
        enabled: deepgramConfig.enabled
      };

      if (deepgramConfig.deepgram_api_key) {
        updateData.deepgram_api_key = deepgramConfig.deepgram_api_key;
      }

      await axios.put(`${API}/master/deepgram-config`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDeepgramSuccess('Configurações do Deepgram salvas com sucesso!');
      setDeepgramConfig(prev => ({
        ...prev,
        deepgram_api_key: ''
      }));
      
      setTimeout(() => {
        loadDeepgramConfig();
        setDeepgramSuccess('');
      }, 2000);

    } catch (err) {
      console.error('Error saving Deepgram config:', err);
      setDeepgramError(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingDeepgram(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/system-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setConfigured(true);
        setMaskedData({
          token: response.data.mp_access_token_global_masked,
          key: response.data.mp_public_key_global_masked
        });
      }
      
      setSettings({
        mp_access_token_global: '',
        mp_public_key_global: '',
        subscription_price: response.data.subscription_price || 99.00,
        menu_base_url: response.data.menu_base_url || ''
      });
      
      // Carregar instruções globais da IA
      setGlobalAiInstructions(response.data.global_ai_instructions || '');
      setGlobalAiInstructionsEnabled(response.data.global_ai_instructions_enabled !== false);
      
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!settings.mp_access_token_global && !configured) {
        setError('Access Token é obrigatório');
        return;
      }

      if (settings.subscription_price <= 0) {
        setError('Preço da assinatura deve ser maior que zero');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Only send fields that have values
      const updateData = {
        subscription_price: settings.subscription_price
      };

      if (settings.mp_access_token_global) {
        updateData.mp_access_token_global = settings.mp_access_token_global;
      }

      if (settings.mp_public_key_global) {
        updateData.mp_public_key_global = settings.mp_public_key_global;
      }

      // Sempre enviar menu_base_url (pode ser string vazia para resetar)
      updateData.menu_base_url = settings.menu_base_url || '';

      // For updates, require at least the token
      if (!configured && !updateData.mp_access_token_global) {
        setError('Access Token do Mercado Pago é obrigatório');
        return;
      }

      await axios.put(`${API}/master/system-settings`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Configurações salvas com sucesso!');
      setSettings({
        mp_access_token_global: '',
        mp_public_key_global: '',
        subscription_price: settings.subscription_price,
        menu_base_url: settings.menu_base_url
      });
      
      // Reload to get masked values
      setTimeout(() => {
        loadSettings();
        setSuccess('');
      }, 2000);

    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  // Função para salvar instruções globais da IA
  const handleSaveAiInstructions = async () => {
    try {
      setSavingAiInstructions(true);
      setAiInstructionsError('');
      setAiInstructionsSuccess('');

      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/master/system-settings`, {
        global_ai_instructions: globalAiInstructions,
        global_ai_instructions_enabled: globalAiInstructionsEnabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAiInstructionsSuccess('Instruções globais da IA salvas com sucesso!');
      
      setTimeout(() => {
        setAiInstructionsSuccess('');
      }, 3000);

    } catch (err) {
      console.error('Error saving AI instructions:', err);
      setAiInstructionsError(err.response?.data?.detail || 'Erro ao salvar instruções da IA');
    } finally {
      setSavingAiInstructions(false);
    }
  };

  // Função para carregar diagnóstico de webhooks
  const loadDiagnostico = async () => {
    try {
      setLoadingDiagnostico(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/mercadopago/diagnostico-webhook`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnostico(response.data);
    } catch (err) {
      console.error('Erro ao carregar diagnóstico:', err);
      toast.error('Erro ao carregar diagnóstico');
    } finally {
      setLoadingDiagnostico(false);
    }
  };

  // Função para sincronizar pagamentos pendentes
  const handleSincronizarPagamentos = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/mercadopago/sincronizar-pagamentos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSyncResult(response.data);
      
      if (response.data.aprovados > 0) {
        toast.success(`${response.data.aprovados} assinatura(s) renovada(s)!`);
      } else {
        toast.info('Nenhum pagamento aprovado encontrado');
      }
    } catch (err) {
      console.error('Erro ao sincronizar:', err);
      toast.error(err.response?.data?.detail || 'Erro ao sincronizar pagamentos');
    } finally {
      setSyncing(false);
    }
  };

  // Função para alterar senha do master
  const handleChangePassword = async () => {
    try {
      setChangingPassword(true);
      setPasswordError('');
      setPasswordSuccess('');

      // Validações
      if (!passwordData.current_password) {
        setPasswordError('Digite sua senha atual');
        return;
      }

      if (!passwordData.new_password) {
        setPasswordError('Digite a nova senha');
        return;
      }

      if (passwordData.new_password.length < 6) {
        setPasswordError('A nova senha deve ter pelo menos 6 caracteres');
        return;
      }

      if (passwordData.new_password !== passwordData.confirm_password) {
        setPasswordError('A nova senha e a confirmação não coincidem');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/master/change-password`, passwordData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordSuccess('Senha alterada com sucesso!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });

      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setPasswordSuccess('');
      }, 3000);

    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordError(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Configurações do Sistema</h1>
          <p className="text-gray-400 mt-1">Configure credenciais globais para Evolution API e Mercado Pago</p>
        </div>
      </div>

      {/* Seção de Alteração de Senha do Master */}
      <div className="glass-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Alterar Senha do Administrador</h2>
              <p className="text-gray-400 text-sm">Atualize sua senha de acesso ao painel master</p>
            </div>
          </div>

          {/* Mensagens de sucesso/erro */}
          {passwordSuccess && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {passwordSuccess}
              </p>
            </div>
          )}
          
          {passwordError && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {passwordError}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {/* Senha Atual */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Senha Atual *
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  placeholder="Digite sua senha atual"
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Nova Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  placeholder="Digite a nova senha (mínimo 6 caracteres)"
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Confirmar Nova Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmar Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  placeholder="Confirme a nova senha"
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Toggle para mostrar/ocultar senhas */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showPasswords"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
                className="rounded bg-gray-800 border-gray-700 text-purple-500 focus:ring-purple-500"
              />
              <label htmlFor="showPasswords" className="text-sm text-gray-400 cursor-pointer">
                Mostrar senhas
              </label>
            </div>

            {/* Botão de Salvar */}
            <div className="pt-4">
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {changingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Alterando...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Alterar Senha
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Seção de Instruções Globais da IA */}
      <div className="glass-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <span className="text-2xl">🤖</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Instruções Globais da IA</h2>
              <p className="text-gray-400 text-sm">Regras obrigatórias que valem para TODAS as lojas</p>
            </div>
            {globalAiInstructionsEnabled && globalAiInstructions && (
              <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                ✓ Ativo
              </span>
            )}
          </div>

          {/* Mensagens de sucesso/erro */}
          {aiInstructionsSuccess && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {aiInstructionsSuccess}
              </p>
            </div>
          )}
          
          {aiInstructionsError && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {aiInstructionsError}
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
            <p className="text-indigo-300 text-sm">
              <strong>🎯 Como funciona:</strong> As instruções definidas aqui são aplicadas automaticamente 
              a TODAS as IAs de todas as lojas, antes da personalização de cada lojista.
            </p>
            <p className="text-indigo-300/80 text-xs mt-2">
              <strong>📋 Hierarquia:</strong> Instruções Globais (Master) → Personalização do Lojista → Cardápio da Loja
            </p>
          </div>

          {/* Exemplo de uso */}
          <div className="mb-6 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
            <p className="text-gray-400 text-sm mb-2"><strong>💡 Exemplos de regras:</strong></p>
            <ul className="text-gray-500 text-xs space-y-1 list-disc list-inside">
              <li>NUNCA confirme produtos que não estão listados no cardápio</li>
              <li>Se cliente perguntar sobre produto inexistente, diga que não trabalha e sugira alternativas</li>
              <li>NUNCA negocie preços - os valores são fixos</li>
              <li>Sempre confirme o pedido completo antes de finalizar</li>
            </ul>
          </div>

          <div className="space-y-4">
            {/* Toggle Ativo/Inativo */}
            <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div>
                <p className="text-white font-medium">Instruções Globais Ativas</p>
                <p className="text-gray-400 text-sm">
                  {globalAiInstructionsEnabled 
                    ? 'As regras estão sendo aplicadas em todas as lojas' 
                    : 'As regras estão desativadas temporariamente'}
                </p>
              </div>
              <button
                onClick={() => setGlobalAiInstructionsEnabled(!globalAiInstructionsEnabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  globalAiInstructionsEnabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    globalAiInstructionsEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Textarea para instruções */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Instruções para a IA
              </label>
              <textarea
                value={globalAiInstructions}
                onChange={(e) => setGlobalAiInstructions(e.target.value)}
                placeholder="Digite as regras que todas as IAs devem seguir...&#10;&#10;Exemplo:&#10;1. NUNCA confirme produtos que não estão no cardápio&#10;2. Se cliente perguntar sobre produto inexistente, sugira alternativas&#10;3. Sempre seja educado e profissional"
                rows={10}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-gray-500 text-xs mt-2">
                {globalAiInstructions.length} caracteres
              </p>
            </div>

            {/* Botão Salvar */}
            <div className="pt-4">
              <button
                onClick={handleSaveAiInstructions}
                disabled={savingAiInstructions}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {savingAiInstructions ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Salvar Instruções Globais
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Seção Deepgram - Transcrição de Áudio */}
      <div className="glass-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
              <span className="text-2xl">🎤</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Deepgram - Transcrição de Áudio</h2>
              <p className="text-gray-400 text-sm">Configure para que a IA responda mensagens de áudio no WhatsApp</p>
            </div>
            {deepgramConfigured && deepgramConfig.enabled && (
              <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                ✓ Ativo
              </span>
            )}
          </div>

          {/* Mensagens de sucesso/erro */}
          {deepgramSuccess && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {deepgramSuccess}
              </p>
            </div>
          )}
          
          {deepgramError && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {deepgramError}
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <p className="text-cyan-300 text-sm">
              <strong>🎯 Como funciona:</strong> Quando um cliente envia um áudio no WhatsApp, 
              o Deepgram transcreve automaticamente para texto e a IA responde normalmente.
            </p>
            <p className="text-cyan-200 text-xs mt-2">
              📌 Obtenha sua API Key em: <a href="https://console.deepgram.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">console.deepgram.com</a>
            </p>
          </div>

          <div className="space-y-4">
            {/* Toggle Habilitado */}
            <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
              <div>
                <p className="text-white font-medium">Habilitar Transcrição de Áudio</p>
                <p className="text-gray-400 text-sm">Permitir que a IA responda mensagens de voz</p>
              </div>
              <Switch
                checked={deepgramConfig.enabled}
                onCheckedChange={(checked) => setDeepgramConfig({...deepgramConfig, enabled: checked})}
              />
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Deepgram API Key {!deepgramConfigured && '*'}
              </label>
              {deepgramConfigured && (
                <div className="mb-2 p-2 bg-gray-800/50 rounded text-gray-400 text-sm font-mono">
                  Configurada: {deepgramMaskedKey}
                </div>
              )}
              <input
                type="password"
                value={deepgramConfig.deepgram_api_key}
                onChange={(e) => setDeepgramConfig({...deepgramConfig, deepgram_api_key: e.target.value})}
                placeholder={deepgramConfigured ? "Digite nova chave para atualizar" : "Cole sua API Key do Deepgram"}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent font-mono"
              />
            </div>

            {/* Botão Salvar */}
            <div className="pt-4">
              <button
                onClick={handleSaveDeepgram}
                disabled={savingDeepgram}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {savingDeepgram ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Salvar Configuração
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Evolution API Section */}
      <EvolutionAPIConfigSection />

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Mercado Pago Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Mercado Pago</h2>
        <p className="text-gray-400">Configurações para renovações de planos</p>
      </div>

      {/* Status Card */}
      {configured && (
        <div className="glass-card border-2 border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/10">
          <div className="flex items-center gap-4 p-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg">Sistema Configurado</h3>
              <p className="text-gray-400 text-sm">Mercado Pago está conectado e funcionando</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Alert */}
      <div className="glass-card border-l-4 border-blue-500 bg-blue-500/10">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Sobre estas configurações</h3>
              <p className="text-gray-400 text-sm mb-2">
                Estas credenciais são usadas para processar <strong className="text-white">renovações de planos dos lojistas</strong>. 
                O dinheiro vai para a conta do Mercado Pago configurada aqui (conta do dono do sistema).
              </p>
              <p className="text-gray-400 text-sm">
                <strong className="text-yellow-400">Importante:</strong> Não confunda com as credenciais que cada loja configura 
                para receber pagamentos dos clientes finais.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Settings Form */}
      <div className="glass-card">
        <div className="p-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Key className="w-5 h-5" />
              Credenciais do Mercado Pago
            </h2>

            {/* Access Token */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Access Token (Obrigatório) *
                </label>
                {configured && !showTokens && (
                  <div className="mb-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm font-mono">{maskedData.token}</span>
                      <button
                        onClick={() => setShowTokens(true)}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Eye size={16} />
                        Atualizar
                      </button>
                    </div>
                  </div>
                )}
                
                {(!configured || showTokens) && (
                  <input
                    type="text"
                    value={settings.mp_access_token_global}
                    onChange={(e) => setSettings({...settings, mp_access_token_global: e.target.value})}
                    placeholder="APP_USR-XXXXX-XXXXXX-XXXXX-XXXXXX"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                  />
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Token de acesso da sua conta Mercado Pago (usado para processar renovações)
                </p>
              </div>

              {/* Public Key */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Public Key (Opcional)
                </label>
                {configured && !showTokens && (
                  <div className="mb-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm font-mono">{maskedData.key}</span>
                      <button
                        onClick={() => setShowTokens(true)}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Eye size={16} />
                        Atualizar
                      </button>
                    </div>
                  </div>
                )}
                
                {(!configured || showTokens) && (
                  <input
                    type="text"
                    value={settings.mp_public_key_global}
                    onChange={(e) => setSettings({...settings, mp_public_key_global: e.target.value})}
                    placeholder="APP_USR-XXXXX-XXXXXX (opcional)"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                  />
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Chave pública (usado para algumas integrações frontend)
                </p>
              </div>
            </div>
          </div>

          <div className="h-px bg-gray-700"></div>

          {/* WhatsApp AI Config Section */}
          <WhatsAppAIConfigSection />

          <div className="h-px bg-gray-700"></div>

          {/* Subscription Price */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Preço da Assinatura
            </h2>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Valor Mensal (R$) *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  R$
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.subscription_price}
                  onChange={(e) => setSettings({...settings, subscription_price: parseFloat(e.target.value)})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-blue-500 text-lg font-semibold"
                />
              </div>
              <p className="text-gray-500 text-xs">
                Valor cobrado mensalmente de cada loja para renovação do plano
              </p>
            </div>

            {/* URL Base do Cardápio */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                URL Base do Cardápio (WhatsApp)
              </label>
              <input
                type="url"
                value={settings.menu_base_url}
                onChange={(e) => setSettings({...settings, menu_base_url: e.target.value})}
                placeholder="https://seudominio.com"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
              <p className="text-gray-500 text-xs">
                URL do domínio principal que será usada nos links do cardápio enviados via WhatsApp.
                Ex: https://zapdelivery.shop → Link: https://zapdelivery.shop/menu/nomeloja
              </p>
              {!settings.menu_base_url && (
                <p className="text-yellow-500 text-xs">
                  ⚠️ Se não configurado, será usado o domínio do ambiente atual (pode mudar em deploys)
                </p>
              )}
            </div>
          </div>

          {/* Feedback Messages */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-400 font-medium">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-green-400 font-medium">{success}</p>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  {configured ? 'Atualizar Configurações' : 'Salvar Configurações'}
                </>
              )}
            </button>

            {showTokens && (
              <button
                onClick={() => {
                  setShowTokens(false);
                  setSettings({
                    ...settings,
                    mp_access_token_global: '',
                    mp_public_key_global: ''
                  });
                }}
                className="px-6 py-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all"
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Ferramentas de Diagnóstico - Pagamentos */}
      <div className="glass-card border border-purple-500/30 bg-purple-500/5">
        <div className="p-6">
          <h3 className="text-purple-400 font-bold mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Diagnóstico de Pagamentos (Webhooks)
          </h3>
          
          <p className="text-gray-400 text-sm mb-4">
            Use estas ferramentas quando pagamentos foram feitos mas o sistema não renovou automaticamente as assinaturas.
          </p>

          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={loadDiagnostico}
              disabled={loadingDiagnostico}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {loadingDiagnostico ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Eye className="w-4 h-4" />
              )}
              Ver Diagnóstico
            </button>
            
            <button
              onClick={handleSincronizarPagamentos}
              disabled={syncing}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {syncing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Sincronizar Pagamentos Pendentes
            </button>
          </div>

          {/* Resultado do Diagnóstico */}
          {diagnostico && (
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
              <h4 className="text-white font-semibold mb-2">Resultado do Diagnóstico</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-yellow-400 text-2xl font-bold">{diagnostico.estatisticas?.pendentes || 0}</p>
                  <p className="text-gray-400 text-xs">Pendentes</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-green-400 text-2xl font-bold">{diagnostico.estatisticas?.aprovados || 0}</p>
                  <p className="text-gray-400 text-xs">Aprovados</p>
                </div>
              </div>

              {diagnostico.verificacoes?.length > 0 && (
                <div className="mb-3">
                  <p className="text-gray-400 text-sm">Verificações:</p>
                  <ul className="text-sm space-y-1 mt-1">
                    {diagnostico.verificacoes.map((v, i) => (
                      <li key={i} className="text-gray-300">{v}</li>
                    ))}
                  </ul>
                </div>
              )}

              {diagnostico.recomendacoes?.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                  <p className="text-yellow-400 text-sm font-medium mb-1">Recomendações:</p>
                  <ul className="text-sm space-y-1">
                    {diagnostico.recomendacoes.map((r, i) => (
                      <li key={i} className="text-yellow-300/80">{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {diagnostico.webhook_url_esperada && (
                <div className="mt-3 bg-blue-500/10 border border-blue-500/30 rounded p-3">
                  <p className="text-blue-400 text-sm font-medium">URL do Webhook (configure no Mercado Pago):</p>
                  <code className="text-blue-300 text-xs break-all">{diagnostico.webhook_url_esperada}</code>
                </div>
              )}
            </div>
          )}

          {/* Resultado da Sincronização */}
          {syncResult && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-3">Resultado da Sincronização</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-blue-400 text-xl font-bold">{syncResult.total_verificados}</p>
                  <p className="text-gray-400 text-xs">Verificados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-green-400 text-xl font-bold">{syncResult.aprovados}</p>
                  <p className="text-gray-400 text-xs">Aprovados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-red-400 text-xl font-bold">{syncResult.rejeitados}</p>
                  <p className="text-gray-400 text-xs">Rejeitados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-orange-400 text-xl font-bold">{syncResult.cancelados}</p>
                  <p className="text-gray-400 text-xs">Cancelados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-yellow-400 text-xl font-bold">{syncResult.ainda_pendentes}</p>
                  <p className="text-gray-400 text-xs">Pendentes</p>
                </div>
              </div>

              {syncResult.aprovados > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                  <p className="text-green-400 text-sm">
                    ✅ {syncResult.aprovados} assinatura(s) foram renovadas automaticamente!
                  </p>
                </div>
              )}

              {syncResult.detalhes?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-gray-400 text-sm cursor-pointer hover:text-white">
                    Ver detalhes ({syncResult.detalhes.length} itens)
                  </summary>
                  <div className="mt-2 max-h-48 overflow-y-auto">
                    {syncResult.detalhes.map((d, i) => (
                      <div key={i} className="text-xs text-gray-400 border-b border-gray-700 py-2">
                        <span className="text-white">#{d.payment_id}</span> - {d.status_novo || d.erro}
                        {d.acao && <span className="text-green-400 ml-2">→ {d.acao}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Help Card */}
      <div className="glass-card border border-yellow-500/30 bg-yellow-500/5">
        <div className="p-6">
          <h3 className="text-yellow-400 font-bold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Como obter as credenciais do Mercado Pago?
          </h3>
          <ol className="text-gray-400 text-sm space-y-2 list-decimal list-inside">
            <li>Acesse sua conta Mercado Pago</li>
            <li>Vá em <strong className="text-white">Seu negócio → Configurações → Credenciais</strong></li>
            <li>Escolha <strong className="text-white">Credenciais de produção</strong></li>
            <li>Copie o <strong className="text-white">Access Token</strong> e a <strong className="text-white">Public Key</strong></li>
            <li>Cole aqui e salve</li>
          </ol>
          <p className="text-yellow-400 text-sm mt-4">
            ⚠️ Mantenha essas credenciais seguras e nunca compartilhe com terceiros!
          </p>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// GALERIA DE IMAGENS - MASTER
// ============================================================

export const MasterGalleryContent = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Estados para upload de imagem
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Estados para Import/Export CSV
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const csvInputRef = React.useRef(null);
  
  // Estados para nova categoria
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  // ====== ESTADOS PARA IMPORTAR DE LOJAS ======
  const [activeTab, setActiveTab] = useState('gallery'); // 'gallery' ou 'import'
  const [productsWithImages, setProductsWithImages] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [restaurantsList, setRestaurantsList] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [importFilters, setImportFilters] = useState({
    restaurant_id: '',
    category: '',
    search: ''
  });
  const [importPage, setImportPage] = useState(1);
  const [importTotalPages, setImportTotalPages] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [importingProducts, setImportingProducts] = useState(false);
  const [showImportProductModal, setShowImportProductModal] = useState(false);
  const [productToImport, setProductToImport] = useState(null);
  const [importFormData, setImportFormData] = useState({ name: '', keywords: '', category: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    image_url: ''
  });

  // Categorias predefinidas
  const predefinedCategories = [
    'hamburgueres',
    'pizzas',
    'acai',
    'bebidas',
    'sobremesas',
    'porcoes',
    'marmitas',
    'lanches',
    'salgados',
    'doces',
    'outros'
  ];

  const fetchImages = async () => {
    setLoading(true);
    try {
      let url = `${API}/master/gallery`;
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setImages(response.data);
      
      // Extrair categorias únicas
      const uniqueCategories = [...new Set(response.data.map(img => img.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Erro ao carregar galeria:', error);
      toast.error('Erro ao carregar galeria');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [filterCategory, searchTerm]);

  // ====== FUNÇÕES PARA IMPORTAR DE LOJAS ======
  
  // Buscar lista de restaurantes para filtro
  const fetchRestaurantsList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/gallery/restaurants-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestaurantsList(response.data.restaurants || []);
    } catch (error) {
      console.error('Erro ao carregar restaurantes:', error);
    }
  };

  // Buscar categorias de produtos para filtro
  const fetchProductCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/gallery/product-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductCategories(response.data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  // Buscar produtos com imagem
  const fetchProductsWithImages = async (page = 1) => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (importFilters.restaurant_id) params.append('restaurant_id', importFilters.restaurant_id);
      if (importFilters.category) params.append('category', importFilters.category);
      if (importFilters.search) params.append('search', importFilters.search);
      
      const response = await axios.get(`${API}/master/gallery/products-with-images?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProductsWithImages(response.data.products || []);
      setImportTotalPages(response.data.total_pages || 1);
      setImportPage(page);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast.error('Erro ao carregar produtos das lojas');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Carregar dados quando mudar para aba de importação
  useEffect(() => {
    if (activeTab === 'import') {
      fetchRestaurantsList();
      fetchProductCategories();
      fetchProductsWithImages(1);
    }
  }, [activeTab]);

  // Recarregar produtos quando filtros mudam
  useEffect(() => {
    if (activeTab === 'import') {
      fetchProductsWithImages(1);
    }
  }, [importFilters]);

  // Gerar palavras-chave automáticas baseado no nome e categoria
  const generateKeywords = (name, category, description = '') => {
    const keywords = [];
    // Adicionar nome em minúsculas
    if (name) {
      keywords.push(name.toLowerCase());
      // Adicionar palavras individuais do nome (mais de 2 caracteres)
      name.toLowerCase().split(/[\s\-\_\,\.]+/).forEach(word => {
        if (word.length > 2 && !keywords.includes(word)) {
          keywords.push(word);
        }
      });
    }
    // Adicionar categoria
    if (category) {
      const catLower = category.toLowerCase();
      if (!keywords.includes(catLower)) {
        keywords.push(catLower);
      }
    }
    // Extrair palavras da descrição (se disponível)
    if (description) {
      const descWords = description.toLowerCase().split(/[\s\-\_\,\.]+/);
      descWords.forEach(word => {
        if (word.length > 3 && !keywords.includes(word) && keywords.length < 15) {
          keywords.push(word);
        }
      });
    }
    // Adicionar variações comuns de produtos alimentícios
    const variations = {
      'hamburguer': ['burger', 'lanche', 'sanduiche'],
      'x-': ['cheese', 'queijo', 'lanche'],
      'pizza': ['massa', 'italiana'],
      'açaí': ['acai', 'fruta', 'gelado'],
      'suco': ['bebida', 'fruta', 'natural'],
      'refri': ['refrigerante', 'bebida', 'coca'],
      'batata': ['frita', 'fritas', 'acompanhamento'],
      'salada': ['verde', 'fit', 'saudavel'],
      'combo': ['promocao', 'kit', 'oferta']
    };
    const nameLower = name?.toLowerCase() || '';
    Object.entries(variations).forEach(([key, values]) => {
      if (nameLower.includes(key)) {
        values.forEach(v => {
          if (!keywords.includes(v)) keywords.push(v);
        });
      }
    });
    return keywords.slice(0, 20).join(', ');
  };

  // Abrir modal de importação individual
  const openImportProductModal = (product) => {
    setProductToImport(product);
    setImportFormData({
      name: product.name,
      keywords: generateKeywords(product.name, product.category, product.description),
      category: product.category || 'Geral'
    });
    setShowImportProductModal(true);
  };

  // Importar produto individual
  const handleImportProduct = async () => {
    if (!productToImport) return;
    
    setImportingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/gallery/import-from-product`, {
        product_id: productToImport.id,
        name: importFormData.name,
        keywords: importFormData.keywords,
        category: importFormData.category
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Imagem importada para galeria!');
        setShowImportProductModal(false);
        setProductToImport(null);
        // Atualizar lista para marcar como importado
        fetchProductsWithImages(importPage);
        // Atualizar galeria também
        fetchImages();
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar imagem');
    } finally {
      setImportingProducts(false);
    }
  };

  // Toggle seleção de produto para importação em lote
  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, {
          product_id: product.id,
          name: product.name,
          keywords: generateKeywords(product.name, product.category, product.description),
          category: product.category || 'Geral'
        }];
      }
    });
  };

  // Importar múltiplos produtos selecionados
  const handleImportSelected = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    setImportingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/gallery/import-multiple-products`, 
        selectedProducts,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`${response.data.imported} imagens importadas!`);
        if (response.data.skipped > 0) {
          toast.info(`${response.data.skipped} já existiam na galeria`);
        }
        setSelectedProducts([]);
        fetchProductsWithImages(importPage);
        fetchImages();
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error('Erro ao importar imagens');
    } finally {
      setImportingProducts(false);
    }
  };

  // Função para lidar com seleção de arquivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Formato não suportado. Use JPG, PNG ou WEBP');
        return;
      }
      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo: 5MB');
        return;
      }
      setSelectedFile(file);
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Função para fazer upload
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/master/gallery/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        setSelectedFile(null);
        setImagePreview(null);
        toast.success('Upload realizado!');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao fazer upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Limpar preview
  const clearImagePreview = () => {
    setSelectedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingImage) {
        await axios.put(`${API}/master/gallery/${editingImage.id}`, formData);
        toast.success('Imagem atualizada!');
      } else {
        await axios.post(`${API}/master/gallery`, formData);
        toast.success('Imagem adicionada!');
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({ name: '', description: '', category: '', image_url: '' });
      setEditingImage(null);
      clearImagePreview();
      fetchImages();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar imagem');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta imagem?')) return;
    
    try {
      await axios.delete(`${API}/master/gallery/${imageId}`);
      toast.success('Imagem excluída!');
      fetchImages();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir imagem');
    }
  };

  const openEditModal = (image) => {
    setEditingImage(image);
    setFormData({
      name: image.name,
      description: image.description,
      category: image.category,
      image_url: image.image_url
    });
    setShowEditModal(true);
  };

  // Exportar CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/gallery/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Criar link de download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'galeria_imagens.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  // Importar CSV
  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Selecione um arquivo CSV');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API}/master/gallery/import-csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} imagens importadas!`);
        fetchImages();
      } else {
        toast.warning('Nenhuma imagem foi importada');
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar CSV');
    } finally {
      setImporting(false);
    }
  };

  // Download template CSV
  const downloadTemplate = () => {
    const template = `nome,categoria,palavras_chave,url_cloudinary
X-Bacon Artesanal,hamburgueres,"hamburguer bacon cheddar artesanal",https://res.cloudinary.com/exemplo/image/upload/v123/xbacon.jpg
Pizza Margherita,pizzas,"pizza margherita tomate manjericao mussarela",https://res.cloudinary.com/exemplo/image/upload/v123/margherita.jpg
Açaí com Morango,acai,"acai morango granola banana",https://res.cloudinary.com/exemplo/image/upload/v123/acai.jpg

INSTRUÇÕES:
- nome: Nome da imagem (obrigatório)
- categoria: hamburgueres, pizzas, acai, bebidas, sobremesas, porcoes, marmitas, lanches, salgados, doces, outros
- palavras_chave: Separe por espaços ou vírgulas (usado para vincular aos produtos)
- url_cloudinary: URL completa da imagem no Cloudinary (OBRIGATÓRIO - apenas URLs do Cloudinary são aceitas)`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_galeria.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="space-y-6 pb-8" style={{ minHeight: '100%', WebkitOverflowScrolling: 'touch' }}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Galeria de Imagens</h1>
          <p className="text-gray-400 mt-1">Gerencie imagens para os lojistas utilizarem</p>
        </div>
        {activeTab === 'gallery' && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleExportCSV}
              disabled={exporting || images.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Exportar CSV</span>
            </button>
            <button
              onClick={() => {
                setImportFile(null);
                setImportResult(null);
                setShowImportModal(true);
              }}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              data-testid="import-csv-btn"
            >
              <FileSpreadsheet size={16} />
              <span className="hidden sm:inline">Importar CSV</span>
            </button>
            <button
              onClick={() => {
                setFormData({ name: '', description: '', category: '', image_url: '' });
                setEditingImage(null);
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              data-testid="add-gallery-image-btn"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Nova Imagem</span>
            </button>
          </div>
        )}
        {activeTab === 'import' && selectedProducts.length > 0 && (
          <button
            onClick={handleImportSelected}
            disabled={importingProducts}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {importingProducts ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            ) : (
              <Download size={16} />
            )}
            Importar {selectedProducts.length} Selecionados
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('gallery')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'gallery'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-800/50 text-gray-400 hover:text-white'
          }`}
        >
          📷 Galeria ({images.length})
        </button>
        <button
          onClick={() => setActiveTab('import')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'import'
              ? 'bg-green-600 text-white'
              : 'bg-gray-800/50 text-gray-400 hover:text-white'
          }`}
        >
          🏪 Importar de Lojas
        </button>
      </div>

      {/* Conteúdo baseado na aba ativa */}
      {activeTab === 'gallery' ? (
        <>
          {/* Filtros da Galeria */}
          <div className="glass-card p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                  />
                </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
          >
            <option value="">Todas categorias</option>
            {predefinedCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid de Imagens */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : images.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <ImageIcon size={64} className="mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl text-gray-400 mb-2">Nenhuma imagem na galeria</h3>
          <p className="text-gray-500 text-sm">Clique em "Nova Imagem" para adicionar</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map(image => (
            <div key={image.id} className="glass-card overflow-hidden group">
              <div className="aspect-square relative">
                <img
                  src={image.image_url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => openEditModal(image)}
                    className="p-2 bg-blue-600 rounded-full hover:bg-blue-700"
                  >
                    <Edit size={16} className="text-white" />
                  </button>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-red-600 rounded-full hover:bg-red-700"
                  >
                    <Trash2 size={16} className="text-white" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <h3 className="text-white text-sm font-medium truncate">{image.name}</h3>
                <p className="text-gray-500 text-xs mt-1">{image.category}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {image.tags?.slice(0, 3).map((tag, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded">
                      {tag}
                    </span>
                  ))}
                  {image.tags?.length > 3 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded">
                      +{image.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      ) : (
        /* ====== ABA IMPORTAR DE LOJAS ====== */
        <>
          {/* Info Box */}
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p className="text-green-300 text-sm">
              <strong>💡 Dica:</strong> Selecione imagens de produtos das lojas para adicionar à galeria global. 
              O nome e categoria serão usados como palavras-chave automáticas.
            </p>
          </div>

          {/* Filtros de Importação */}
          <div className="glass-card p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar produto..."
                    value={importFilters.search}
                    onChange={(e) => setImportFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                  />
                </div>
              </div>
              <select
                value={importFilters.restaurant_id}
                onChange={(e) => setImportFilters(prev => ({ ...prev, restaurant_id: e.target.value }))}
                className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white min-w-[180px]"
              >
                <option value="">Todas as lojas</option>
                {restaurantsList.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.product_count})
                  </option>
                ))}
              </select>
              <select
                value={importFilters.category}
                onChange={(e) => setImportFilters(prev => ({ ...prev, category: e.target.value }))}
                className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white min-w-[150px]"
              >
                <option value="">Todas categorias</option>
                {productCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Lista de Produtos */}
          {loadingProducts ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
          ) : productsWithImages.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Store size={64} className="mx-auto mb-4 text-gray-600" />
              <h3 className="text-xl text-gray-400 mb-2">Nenhum produto com imagem encontrado</h3>
              <p className="text-gray-500 text-sm">Tente ajustar os filtros ou aguarde os lojistas adicionarem imagens</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {productsWithImages.map(product => {
                  const isSelected = selectedProducts.some(p => p.product_id === product.id);
                  return (
                    <div 
                      key={product.id} 
                      className={`glass-card overflow-hidden transition-all ${
                        product.already_in_gallery ? 'opacity-50' : ''
                      } ${isSelected ? 'ring-2 ring-green-500' : ''}`}
                    >
                      <div className="flex gap-4 p-4">
                        {/* Imagem */}
                        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-800">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium truncate">{product.name}</h3>
                          <p className="text-gray-500 text-sm truncate">{product.restaurant_name}</p>
                          <p className="text-gray-400 text-xs mt-1">{product.category || 'Sem categoria'}</p>
                          {product.already_in_gallery && (
                            <span className="inline-block mt-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                              ✓ Já na galeria
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Ações */}
                      {!product.already_in_gallery && (
                        <div className="flex gap-2 p-3 pt-0">
                          <button
                            onClick={() => toggleProductSelection(product)}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              isSelected
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {isSelected ? '✓ Selecionado' : 'Selecionar'}
                          </button>
                          <button
                            onClick={() => openImportProductModal(product)}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            Importar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Paginação */}
              {importTotalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <button
                    onClick={() => fetchProductsWithImages(importPage - 1)}
                    disabled={importPage === 1}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="px-4 py-2 text-gray-400">
                    Página {importPage} de {importTotalPages}
                  </span>
                  <button
                    onClick={() => fetchProductsWithImages(importPage + 1)}
                    disabled={importPage === importTotalPages}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modal Importar Produto Individual */}
      <Dialog open={showImportProductModal} onOpenChange={setShowImportProductModal}>
        <DialogContent className="bg-gray-900 border border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Importar para Galeria</DialogTitle>
          </DialogHeader>
          
          {productToImport && (
            <div className="space-y-4 py-4">
              {/* Preview da imagem */}
              <div className="flex gap-4 items-start">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                  <img
                    src={productToImport.image_url}
                    alt={productToImport.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Produto original:</p>
                  <p className="text-white font-medium">{productToImport.name}</p>
                  <p className="text-gray-500 text-xs">{productToImport.restaurant_name}</p>
                </div>
              </div>

              {/* Formulário */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Nome na Galeria</label>
                  <input
                    type="text"
                    value={importFormData.name}
                    onChange={(e) => setImportFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Palavras-chave (separadas por vírgula)</label>
                  <input
                    type="text"
                    value={importFormData.keywords}
                    onChange={(e) => setImportFormData(prev => ({ ...prev, keywords: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    placeholder="hamburguer, lanche, bacon..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Categoria</label>
                  <select
                    value={importFormData.category}
                    onChange={(e) => setImportFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    {predefinedCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowImportProductModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleImportProduct}
                  disabled={importingProducts || !importFormData.name}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {importingProducts ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <>
                      <Download size={16} />
                      Importar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Adicionar/Editar */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowAddModal(false);
          setShowEditModal(false);
          setEditingImage(null);
          setNewCategory('');
          setShowNewCategoryInput(false);
        }
      }}>
        <DialogContent className="bg-gray-900 border border-gray-800 max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white text-xl">
              {editingImage ? 'Editar Imagem' : 'Nova Imagem'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Área de Upload/Preview */}
            <div className="space-y-3">
              <label className="block text-sm text-gray-300 mb-1">Imagem *</label>
              
              {/* Preview da imagem (upload ou URL) */}
              {(imagePreview || formData.image_url) && (
                <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-800">
                  <img
                    src={imagePreview || formData.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={clearImagePreview}
                      className="absolute top-2 right-2 p-1 bg-red-600 rounded-full hover:bg-red-700"
                    >
                      <XCircle size={16} className="text-white" />
                    </button>
                  )}
                </div>
              )}
              
              {/* Área de upload */}
              {!imagePreview && !formData.image_url && (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-gray-500 transition-colors"
                >
                  <Upload size={28} className="mx-auto mb-2 text-gray-500" />
                  <p className="text-gray-400 text-sm">Clique para fazer upload</p>
                  <p className="text-gray-500 text-xs mt-1">JPG, PNG ou WEBP (máx. 5MB)</p>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Botão de upload se arquivo selecionado */}
              {selectedFile && !uploading && (
                <button
                  type="button"
                  onClick={handleUpload}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Upload size={16} />
                  Enviar Imagem
                </button>
              )}
              
              {uploading && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-gray-400">Enviando...</span>
                </div>
              )}
              
              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-gray-900 text-gray-500">ou cole uma URL</span>
                </div>
              </div>
              
              {/* URL Input */}
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Nome *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: X-Bacon Artesanal"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Categoria *</label>
              {!showNewCategoryInput ? (
                <div className="flex gap-2">
                  <select
                    required={!newCategory}
                    value={formData.category}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setShowNewCategoryInput(true);
                        setFormData({...formData, category: ''});
                      } else {
                        setFormData({...formData, category: e.target.value});
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="">Selecione...</option>
                    {predefinedCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__new__" className="text-green-400">+ Nova categoria</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={newCategory}
                    onChange={(e) => {
                      setNewCategory(e.target.value);
                      setFormData({...formData, category: e.target.value.toLowerCase().trim()});
                    }}
                    placeholder="Nome da nova categoria"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategory('');
                      setFormData({...formData, category: ''});
                    }}
                    className="px-3 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">
                Descrição (palavras-chave) *
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="hamburguer bacon cheddar alface tomate artesanal gourmet..."
                rows={2}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separe as palavras por espaço. Serão usadas para vincular automaticamente aos produtos.
              </p>
            </div>

            {/* Botões de ação - Fixos no final */}
            <div className="flex gap-3 pt-2 pb-2 sticky bottom-0 bg-gray-900">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                className="flex-1 px-4 py-3 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Importação CSV */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-gray-900 border border-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <FileSpreadsheet size={22} className="text-purple-400" />
              Importar Imagens via CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
              <p className="text-sm text-purple-300">
                <strong>Importante:</strong> Apenas URLs do Cloudinary são aceitas. Use este recurso para reciclar imagens já existentes no Cloudinary.
              </p>
            </div>

            {/* Botão Download Template */}
            <button
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors"
            >
              <Download size={16} />
              Baixar Template CSV
            </button>

            {/* Input de arquivo */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Arquivo CSV</label>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
              />
            </div>

            {/* Resultado da importação */}
            {importResult && (
              <div className={`p-3 rounded-lg ${importResult.imported > 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                <p className={`font-medium ${importResult.imported > 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                  {importResult.message}
                </p>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    <p className="text-xs text-gray-400 mb-1">Erros encontrados:</p>
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-400">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowImportModal(false)}
                className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleImportCSV}
                disabled={!importFile || importing}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Importar
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================
// GERENCIAMENTO UAZAPI - MASTER
// Vincular instâncias UazAPI a lojas individuais
// ============================================================

export const MasterUazAPIContent = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, connected, disconnected, not_configured
  
  // Modal de configuração
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [instanceToken, setInstanceToken] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Modal de teste de mensagem
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('🧪 Mensagem de teste do Meucardapiozap!');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Configuração global
  const [globalConfig, setGlobalConfig] = useState(null);

  useEffect(() => {
    fetchRestaurants();
    fetchGlobalConfig();
  }, []);

  const fetchGlobalConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/evolution-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config global:', error);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/uazapi/restaurants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestaurants(response.data.restaurants || []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
      toast.error('Erro ao carregar lista de lojas');
      setLoading(false);
    }
  };

  const handleOpenConfig = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setInstanceToken('');
    setInstanceName(restaurant.instance_name || '');
    setTestResult(null);
    setShowConfigModal(true);
  };

  const handleTestConnection = async () => {
    if (!instanceToken.trim()) {
      toast.error('Digite o token da instância');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/uazapi/test-connection`, {
        instance_token: instanceToken.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success('Conexão válida!');
      } else {
        toast.error(response.data.message || 'Falha na conexão');
      }
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      setTestResult({ success: false, message: 'Erro ao testar conexão' });
      toast.error('Erro ao testar conexão');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!instanceToken.trim()) {
      toast.error('Digite o token da instância');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/uazapi/link-instance`, {
        restaurant_id: selectedRestaurant.id,
        instance_token: instanceToken.trim(),
        instance_name: instanceName.trim() || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message || 'Instância vinculada com sucesso!');
      setShowConfigModal(false);
      fetchRestaurants();
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (restaurant) => {
    if (!window.confirm(`Deseja realmente desvincular a instância UazAPI da loja "${restaurant.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/master/uazapi/unlink-instance/${restaurant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Instância desvinculada com sucesso!');
      fetchRestaurants();
    } catch (error) {
      console.error('Erro ao desvincular:', error);
      toast.error('Erro ao desvincular instância');
    }
  };

  const handleOpenTestMessage = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setTestPhone('');
    setTestMessage('🧪 Mensagem de teste do Meucardapiozap!\n\nSe você recebeu esta mensagem, a integração UazAPI está funcionando! ✅');
    setShowTestModal(true);
  };

  const handleSendTestMessage = async () => {
    if (!testPhone.trim()) {
      toast.error('Digite o número de WhatsApp');
      return;
    }

    setSendingTest(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/uazapi/send-test-message`, {
        restaurant_id: selectedRestaurant.id,
        phone: testPhone.trim(),
        message: testMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Mensagem enviada com sucesso!');
        setShowTestModal(false);
      } else {
        toast.error(response.data.message || 'Falha ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSendingTest(false);
    }
  };

  // Filtrar restaurantes
  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' ||
                          (filterStatus === 'connected' && r.whatsapp_status === 'connected') ||
                          (filterStatus === 'disconnected' && r.whatsapp_status === 'disconnected') ||
                          (filterStatus === 'not_configured' && !r.has_uazapi_token);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (restaurant) => {
    if (!restaurant.has_uazapi_token) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
          Não configurado
        </span>
      );
    }
    
    switch (restaurant.whatsapp_status) {
      case 'connected':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-1">
            <CheckCircle size={12} /> Conectado
          </span>
        );
      case 'disconnected':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
            <XCircle size={12} /> Desconectado
          </span>
        );
      case 'qrcode':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 flex items-center gap-1">
            <Clock size={12} /> Aguardando QR
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
            {restaurant.whatsapp_status || 'Desconhecido'}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Verificar se UazAPI está configurado globalmente
  if (globalConfig && globalConfig.api_type !== 'uazapi') {
    return (
      <div className="p-6">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 text-center">
          <AlertTriangle className="mx-auto mb-4 text-yellow-400" size={48} />
          <h3 className="text-xl font-bold text-yellow-400 mb-2">UazAPI não está ativo</h3>
          <p className="text-gray-400 mb-4">
            Para gerenciar instâncias UazAPI por loja, primeiro configure o UazAPI como API ativa nas Configurações do Sistema.
          </p>
          <p className="text-sm text-gray-500">
            API atual: <strong className="text-white">{globalConfig.api_type?.toUpperCase() || 'Não configurada'}</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Gerenciar UazAPI por Loja</h2>
          <p className="text-gray-400 text-sm mt-1">
            Vincule instâncias UazAPI criadas no painel uazapi.dev a cada loja
          </p>
        </div>
        
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Total:</span>
          <span className="text-white font-bold">{restaurants.length} lojas</span>
          <span className="text-gray-600">|</span>
          <span className="text-green-400">{restaurants.filter(r => r.has_uazapi_token).length} com UazAPI</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome, subdomínio ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
          />
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
        >
          <option value="all">Todos os status</option>
          <option value="connected">Conectados</option>
          <option value="disconnected">Desconectados</option>
          <option value="not_configured">Não configurados</option>
        </select>
      </div>

      {/* Instruções */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <h4 className="font-medium text-blue-400 mb-2">📋 Como configurar:</h4>
        <ol className="text-sm text-gray-400 space-y-1 list-decimal list-inside">
          <li>Crie uma instância no painel <a href="https://uazapi.dev" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">uazapi.dev</a></li>
          <li>Clique em "Configurar" na loja desejada abaixo</li>
          <li>Copie a <strong className="text-white">URL do Webhook</strong> e cole no painel UazAPI → Webhooks → URL</li>
          <li>No painel UazAPI, ative: <code className="bg-gray-800 px-1 rounded">addUrlEvents</code>, <code className="bg-gray-800 px-1 rounded">addUrlTypesMessages</code>, eventos: <code className="bg-gray-800 px-1 rounded">messages</code></li>
          <li>Cole o <strong className="text-white">Token da Instância</strong> no campo do Meucardapiozap e salve</li>
        </ol>
      </div>

      {/* Lista de Lojas */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Loja</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status WhatsApp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Instância</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredRestaurants.map((restaurant) => (
                <tr key={restaurant.id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-white">{restaurant.name}</p>
                      <p className="text-xs text-gray-500">{restaurant.subdomain} • {restaurant.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(restaurant)}
                  </td>
                  <td className="px-4 py-3">
                    {restaurant.has_uazapi_token ? (
                      <span className="text-sm text-gray-300">{restaurant.instance_name || 'Configurado'}</span>
                    ) : (
                      <span className="text-sm text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {restaurant.has_uazapi_token ? (
                        <>
                          <button
                            onClick={() => handleOpenTestMessage(restaurant)}
                            className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                            title="Enviar mensagem de teste"
                          >
                            <FileText size={16} />
                          </button>
                          <button
                            onClick={() => handleOpenConfig(restaurant)}
                            className="p-2 text-orange-400 hover:bg-orange-500/20 rounded-lg transition-colors"
                            title="Reconfigurar"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleUnlink(restaurant)}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Desvincular"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleOpenConfig(restaurant)}
                          className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg transition-colors flex items-center gap-1"
                        >
                          <Key size={14} />
                          Configurar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRestaurants.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nenhuma loja encontrada com os filtros atuais
          </div>
        )}
      </div>

      {/* Modal de Configuração */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="bg-gray-900 border border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="text-orange-400" size={20} />
              Configurar UazAPI - {selectedRestaurant?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* URL do Webhook Individual */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <label className="block text-sm font-medium text-blue-400 mb-2">
                🔗 URL do Webhook (copie para o painel UazAPI)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`https://meucardapiozap.com.br/api/webhook/uazapi/${selectedRestaurant?.id || ''}`}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`https://meucardapiozap.com.br/api/webhook/uazapi/${selectedRestaurant?.id || ''}`);
                    toast.success('URL copiada!');
                  }}
                  className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  title="Copiar URL"
                >
                  <Copy size={16} />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Cole esta URL no campo "URL" do webhook da instância no painel uazapi.dev
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Token da Instância *
              </label>
              <input
                type="text"
                value={instanceToken}
                onChange={(e) => setInstanceToken(e.target.value)}
                placeholder="Cole o token da instância aqui..."
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Copie do painel uazapi.dev → Sua Instância → Token
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome da Instância (opcional)
              </label>
              <input
                type="text"
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                placeholder="Ex: loja_centro"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Botão Testar Conexão */}
            <button
              onClick={handleTestConnection}
              disabled={testing || !instanceToken.trim()}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {testing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Testar Conexão
                </>
              )}
            </button>

            {/* Resultado do Teste */}
            {testResult && (
              <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <p className={`text-sm ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                  {testResult.success ? '✅' : '❌'} {testResult.message}
                </p>
                {testResult.status && (
                  <p className="text-xs text-gray-400 mt-1">
                    Status: {testResult.status}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={() => setShowConfigModal(false)}
              className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveConfig}
              disabled={saving || !instanceToken.trim()}
              className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Salvar
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Teste de Mensagem */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="bg-gray-900 border border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-blue-400" size={20} />
              Enviar Mensagem de Teste
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-400">
              Loja: <strong className="text-white">{selectedRestaurant?.name}</strong>
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Número do WhatsApp *
              </label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="5538999999999"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mensagem
              </label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={() => setShowTestModal(false)}
              className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSendTestMessage}
              disabled={sendingTest || !testPhone.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sendingTest ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <FileText size={16} />
                  Enviar
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Re-export ProspectionModule
export { default as MasterProspectionContent } from './ProspectionModule';
